import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, Button, Form, Badge, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { amazonAPI } from '../api/amazonAPI'
import { formatDateOnly } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'
import '../styles/selectControl.css'

const STATUS_BADGES = {
    PENDING: 'secondary',
    GENERATING: 'warning',
    DONE: 'success',
    FAILED: 'danger',
    SUBMITTED: 'info',
    PROCESSING: 'warning',
    SKIPPED: 'secondary',
}

function AmazonASINReportListScreen() {
    const { hasPermission } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()

    const urlParams = new URLSearchParams(location.state?.returnFilters || location.search)

    const [accounts, setAccounts] = useState([])
    const [selectedAccountId, setSelectedAccountId] = useState(() => urlParams.get('account') || '')
    const [error, setError] = useState('')

    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [activeQuickDate, setActiveQuickDate] = useState(null)
    const [reportType, setReportType] = useState('standard')

    const [reports, setReports] = useState([])
    const [reportsLoading, setReportsLoading] = useState(false)
    const autoRefreshRef = useRef(null)

    const [currentPage, setCurrentPage] = useState(() => parseInt(urlParams.get('page')) || 1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [filterStartDate, setFilterStartDate] = useState(() => urlParams.get('filterStart') || '')
    const [filterEndDate, setFilterEndDate] = useState(() => urlParams.get('filterEnd') || '')
    const [sortDirection, setSortDirection] = useState(() => urlParams.get('sortDir') || 'desc')
    const [loadingReportId, setLoadingReportId] = useState(null)

    useEffect(() => {
        if (location.state?.returnFilters) {
            navigate(`${location.pathname}${location.state.returnFilters}`, {
                replace: true, state: {}
            })
        }
    }, [])

    useEffect(() => {
        const loadAccounts = async () => {
            const data = await amazonAPI.accounts.getAll()
            if (data) {
                const active = data.filter(a => a.is_active)
                setAccounts(active)
                if (active.length === 1 && !selectedAccountId) {
                    setSelectedAccountId(String(active[0].id))
                }
            }
        }
        loadAccounts()
    }, [])

    useEffect(() => {
        fetchReports()
    }, [selectedAccountId, currentPage, pageSize, filterStartDate, filterEndDate, sortDirection])

    useEffect(() => {
        const hasActive = reports.some(r => ['PENDING', 'GENERATING'].includes(r.status))
        if (autoRefreshRef.current) {
            clearInterval(autoRefreshRef.current)
            autoRefreshRef.current = null
        }
        if (hasActive) {
            autoRefreshRef.current = setInterval(fetchReports, 30000)
        }
        return () => {
            if (autoRefreshRef.current) clearInterval(autoRefreshRef.current)
        }
    }, [reports])

    useEffect(() => {
        updateUrlWithFilters()
    }, [selectedAccountId, currentPage, filterStartDate, filterEndDate, sortDirection])

    const fetchReports = useCallback(async () => {
        setReportsLoading(true)
        try {
            const params = { page: currentPage, page_size: pageSize, sortDirection }
            if (selectedAccountId) params.account_id = selectedAccountId
            if (filterStartDate) params.filter_start_date = filterStartDate
            if (filterEndDate) params.filter_end_date = filterEndDate
            const data = await amazonAPI.asinReports.getAll(params)
            if (data) {
                setReports(data.results || [])
                setTotalItems(data.count || 0)
            }
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setReportsLoading(false)
        }
    }, [selectedAccountId, currentPage, pageSize, filterStartDate, filterEndDate, sortDirection])

    const updateUrlWithFilters = () => {
        const params = new URLSearchParams()
        if (selectedAccountId) params.set('account', selectedAccountId)
        if (filterStartDate) params.set('filterStart', filterStartDate)
        if (filterEndDate) params.set('filterEnd', filterEndDate)
        if (sortDirection !== 'desc') params.set('sortDir', sortDirection)
        if (currentPage > 1) params.set('page', currentPage)
        const search = params.toString()
        navigate(`${location.pathname}${search ? '?' + search : ''}`, { replace: true })
    }

    const getCurrentFilters = () => {
        const params = new URLSearchParams()
        if (selectedAccountId) params.set('account', selectedAccountId)
        if (filterStartDate) params.set('filterStart', filterStartDate)
        if (filterEndDate) params.set('filterEnd', filterEndDate)
        if (sortDirection !== 'desc') params.set('sortDir', sortDirection)
        if (currentPage > 1) params.set('page', currentPage)
        const search = params.toString()
        return search ? '?' + search : ''
    }

    const handleGenerate = async () => {
        if (!selectedAccountId || !startDate || !endDate) {
            setError('Please select an account, start date, and end date')
            return
        }
        setError('')
        setSubmitting(true)
        const start = startDate.toISOString().split('T')[0]
        const end = endDate.toISOString().split('T')[0]
        const data = await amazonAPI.asinReports.generate(selectedAccountId, start, end, reportType)
        if (data) {
            setReports(prev => [data, ...prev])
            setTotalItems(prev => prev + 1)
        } else {
            setError('Failed to generate report')
        }
        setSubmitting(false)
    }

    const handleDeleteReport = async (row) => {
        if (!window.confirm('Delete this report and all its data?')) return
        await amazonAPI.asinReports.delete(row.id)
        fetchReports()
    }

    const hasRetryableSteps = (row) => {
        if (!['DONE', 'FAILED'].includes(row.status)) return false
        return (
            row.data_kiosk_status === 'FAILED' || row.data_kiosk_status === null ||
            row.inventory_report_status === 'FAILED' || row.inventory_report_status === null ||
            row.ad_report_status === 'FAILED' || row.ad_report_status === null ||
            row.inventory_planning_status === 'FAILED' || row.inventory_planning_status === null ||
            row.traffic_status === 'FAILED' || row.traffic_status === null ||
            row.status === 'FAILED'
        )
    }

    const handleCancelReport = async (row) => {
        setLoadingReportId(row.id)
        setError('')
        const result = await amazonAPI.asinReports.cancel(row.id)
        setLoadingReportId(null)
        if (result) fetchReports()
    }

    const handleRetryReport = async (row) => {
        setLoadingReportId(row.id)
        setError('')
        const result = await amazonAPI.asinReports.retry(row.id)
        setLoadingReportId(null)
        if (result) fetchReports()
    }

    const handleSort = (_column, direction) => {
        setSortDirection(direction)
        setCurrentPage(1)
    }

    const getStatusBadge = (status) => (
        <Badge bg={STATUS_BADGES[status] || 'secondary'}>{status}</Badge>
    )

    const stepDotStyle = (status) => {
        const colors = { DONE: '#198754', PROCESSING: '#ffc107', SUBMITTED: '#ffc107', FAILED: '#dc3545' }
        return {
            width: 22, height: 22, borderRadius: '50%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.65rem', color: status === 'PROCESSING' || status === 'SUBMITTED' ? '#000' : '#fff',
            background: colors[status] || '#adb5bd',
        }
    }

    const stepConnectorStyle = (status) => ({
        width: 16, height: 2,
        background: status === 'DONE' ? '#198754' : 'var(--bs-border-color)',
        display: 'inline-block', verticalAlign: 'middle',
    })

    const stepDotContent = (status) => {
        if (status === 'DONE') return <i className="bi bi-check" />
        if (status === 'FAILED') return <i className="bi bi-x" />
        if (status === 'PROCESSING' || status === 'SUBMITTED')
            return <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10, borderWidth: 2 }} />
        return <i className="bi bi-clock" style={{ fontSize: '0.55rem' }} />
    }

    const statusLabel = (status) => {
        if (status === 'DONE') return 'Complete'
        if (status === 'FAILED') return 'Failed'
        if (status === 'PROCESSING' || status === 'SUBMITTED') return 'In progress'
        return status || 'Pending'
    }

    const renderStepIndicator = (row) => {
        const steps = [
            { label: 'Sales', status: row.data_kiosk_status, error: row.data_kiosk_error },
            { label: 'Inventory', status: row.inventory_report_status, error: row.inventory_report_error },
            { label: 'Ads', status: row.ad_report_status, error: row.ad_report_error },
            { label: 'Planning', status: row.inventory_planning_status, error: row.inventory_planning_error },
            { label: 'Traffic', status: row.traffic_status, error: row.traffic_error },
        ]
        return (
            <div style={{ display: 'inline-flex', alignItems: 'center', position: 'relative', paddingBottom: 16 }}>
                {steps.map((step, i) => (
                    <React.Fragment key={step.label}>
                        {i > 0 && <div style={stepConnectorStyle(steps[i - 1].status)} />}
                        <OverlayTrigger placement="top" overlay={
                            <Tooltip>
                                {step.label}: {statusLabel(step.status)}
                                {step.status === 'FAILED' && step.error && (
                                    <div style={{ fontSize: '0.75rem', marginTop: 2 }}>{step.error}</div>
                                )}
                            </Tooltip>
                        }>
                            <div style={{ ...stepDotStyle(step.status), position: 'relative', cursor: 'default' }}>
                                {stepDotContent(step.status)}
                                <span style={{
                                    position: 'absolute', top: 24, fontSize: '0.6rem',
                                    color: 'var(--bs-secondary-color)', whiteSpace: 'nowrap',
                                    left: '50%', transform: 'translateX(-50%)',
                                }}>{step.label}</span>
                            </div>
                        </OverlayTrigger>
                    </React.Fragment>
                ))}
            </div>
        )
    }

    const accountOptions = accounts.map(a => ({ value: String(a.id), label: a.account_name }))

    const reportColumns = [
        { id: 'id', header: 'ID', cell: (row) => row.id },
        { id: 'account', header: 'Account', cell: (row) => row.seller_account_name || '' },
        { id: 'reportType', header: 'Type', cell: (row) => {
            if (row.report_type === 'monthly') return <Badge bg="info">Monthly</Badge>
            return <Badge bg="secondary">Standard</Badge>
        }},
        { id: 'dateRange', header: 'Date Range', sortable: true, cell: (row) => {
            const start = row.start_date ? formatDateOnly(row.start_date) : ''
            const end = row.end_date ? formatDateOnly(row.end_date) : ''
            return start && end ? `${start} – ${end}` : ''
        }},
        { id: 'subReports', header: 'Progress', cell: (row) => renderStepIndicator(row) },
        { id: 'status', header: 'Status', cell: (row) => (
            <>
                {getStatusBadge(row.status)}
                {['PENDING', 'GENERATING'].includes(row.status) && (
                    <Spinner size="sm" className="ms-1" />
                )}
            </>
        )},
{ id: 'createdAt', header: 'Created', cell: (row) => formatDateOnly(row.created_at) },
        { id: 'actions', header: 'Actions', cell: (row) => loadingReportId === row.id ? (
            <Spinner size="sm" />
        ) : (
            <>
                {row.status === 'DONE' && (
                    <Button variant="primary" size="sm" className="me-2"
                        onClick={(e) => { e.stopPropagation(); navigate(`/amazon-asin-report/${row.id}`, { state: { returnFilters: getCurrentFilters() } }) }}>
                        Details
                    </Button>
                )}
                {row.status === 'GENERATING' && hasPermission('amazon.manage') && (
                    <Button variant="outline-danger" size="sm" className="me-2"
                        onClick={(e) => { e.stopPropagation(); handleCancelReport(row) }}>
                        Cancel
                    </Button>
                )}
                {hasRetryableSteps(row) && hasPermission('amazon.manage') && (
                    <Button variant="outline-warning" size="sm" className="me-2"
                        onClick={(e) => { e.stopPropagation(); handleRetryReport(row) }}>
                        Retry
                    </Button>
                )}
                {hasPermission('amazon.manage') && (
                    <Button variant="outline-danger" size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteReport(row) }}>Delete</Button>
                )}
            </>
        )}
    ]

    return (
        <Container fluid className="px-4 py-4">
            <h1 className="mb-3">ASIN Profitability</h1>

            {error && <div className="alert alert-danger">{error}</div>}

            <div style={{ maxWidth: '900px' }}>
                <div className="d-flex align-items-end gap-3 flex-wrap mb-2">
                    <div style={{ minWidth: '220px', flex: 1 }}>
                        <Form.Label>Amazon Account</Form.Label>
                        <Select
                            value={accountOptions.find(opt => opt.value === selectedAccountId) || null}
                            onChange={(opt) => { setSelectedAccountId(opt?.value || ''); setCurrentPage(1) }}
                            options={accountOptions}
                            isClearable
                            placeholder="All Accounts..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div style={{ width: '150px' }}>
                        <Form.Label>Start Date</Form.Label>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => {
                                if (reportType === 'monthly' && date) {
                                    setStartDate(new Date(date.getFullYear(), date.getMonth(), 1))
                                } else {
                                    setStartDate(date)
                                }
                                setActiveQuickDate(null)
                            }}
                            className="form-control"
                            dateFormat={reportType === 'monthly' ? 'MM/yyyy' : 'MM/dd/yyyy'}
                            showMonthYearPicker={reportType === 'monthly'}
                            selectsStart startDate={startDate} endDate={endDate} maxDate={endDate}
                            showMonthDropdown showYearDropdown dropdownMode="select" portalId="root"
                        />
                    </div>
                    <div style={{ width: '150px' }}>
                        <Form.Label>End Date</Form.Label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => {
                                if (reportType === 'monthly' && date) {
                                    setEndDate(new Date(date.getFullYear(), date.getMonth() + 1, 0))
                                } else {
                                    setEndDate(date)
                                }
                                setActiveQuickDate(null)
                            }}
                            className="form-control"
                            dateFormat={reportType === 'monthly' ? 'MM/yyyy' : 'MM/dd/yyyy'}
                            showMonthYearPicker={reportType === 'monthly'}
                            selectsEnd startDate={startDate} endDate={endDate} minDate={startDate}
                            maxDate={new Date()} openToDate={startDate}
                            showMonthDropdown showYearDropdown dropdownMode="select" portalId="root"
                        />
                    </div>
                    <div className="d-flex gap-1 align-items-center" style={{ paddingBottom: '4px' }}>
                        {[{ label: '1M', months: 1 }, { label: '3M', months: 3 }, { label: '6M', months: 6 }].map(({ label, months }) => (
                            <Badge key={months} bg={activeQuickDate === months ? 'primary' : 'light'}
                                style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                                onClick={() => {
                                    const end = new Date(); const start = new Date()
                                    start.setMonth(end.getMonth() - months)
                                    setStartDate(start); setEndDate(end); setActiveQuickDate(months)
                                }}>{label}</Badge>
                        ))}
                    </div>
                    <div style={{ minWidth: '180px' }}>
                        <Form.Label>Report Type</Form.Label>
                        <Form.Select size="sm" value={reportType}
                            onChange={(e) => setReportType(e.target.value)}
                            style={{ height: '38px' }}>
                            <option value="standard">Standard</option>
                            <option value="monthly">Monthly Profitability</option>
                        </Form.Select>
                    </div>
                    {hasPermission('amazon.manage') && (
                        <div>
                            <Button variant="success" onClick={handleGenerate}
                                disabled={submitting || !selectedAccountId || !startDate || !endDate}>
                                {submitting ? (<><Spinner size="sm" className="me-2" />Generating...</>) : 'Generate Report'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <hr className="my-4" />

            <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">Report Requests</h4>
                    <Button variant="outline-secondary" size="sm" onClick={fetchReports} disabled={reportsLoading}>
                        <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                    </Button>
                </div>

                <div className="d-flex mb-3">
                    <div className="d-flex gap-2 rounded p-2" style={{ background: 'var(--bs-tertiary-bg)' }}>
                        <Form.Group>
                            <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Period Start</Form.Label>
                            <Form.Control type="date" size="sm" value={filterStartDate}
                                onChange={(e) => { setFilterStartDate(e.target.value); setCurrentPage(1) }}
                                autoComplete="off" spellCheck="false" />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Period End</Form.Label>
                            <Form.Control type="date" size="sm" value={filterEndDate}
                                onChange={(e) => { setFilterEndDate(e.target.value); setCurrentPage(1) }}
                                autoComplete="off" spellCheck="false" />
                        </Form.Group>
                    </div>
                </div>

                {reportsLoading && reports.length === 0 ? (
                    <div className="text-center py-4"><Spinner animation="border" /></div>
                ) : reports.length === 0 ? (
                    <p className="text-muted">No reports found.</p>
                ) : (
                    <>
                        <div className="table-responsive">
                            <ResizableTable columns={reportColumns} data={reports} tableId="amazon_asin_reports"
                                enableSorting={true} onSort={handleSort}
                                sortColumn="dateRange" sortDirection={sortDirection}
                                initialColumnWidths={{ id: 60, dateRange: 220, status: 140, subReports: 140, createdAt: 150, actions: 300 }}
                                minHeight="auto" striped hover className="table-sm" />
                        </div>
                        <div className="mt-3">
                            <PaginationComponent
                                currentPage={currentPage}
                                totalItems={totalItems}
                                onPageChange={setCurrentPage}
                                onItemsPerPageChange={setPageSize}
                            />
                        </div>
                    </>
                )}
            </div>
        </Container>
    )
}

export default AmazonASINReportListScreen
